/** Session keys + helpers for crew-invite participant context. */

export const CREW_CTX_KEYS = {
  token: 'mvp_customer_crew_token',
  name: 'mvp_customer_crew_name',
  email: 'mvp_customer_crew_email',
  phone: 'mvp_customer_crew_phone',
  jobTitle: 'mvp_customer_crew_job_title',
  department: 'mvp_customer_crew_department',
  country: 'mvp_customer_crew_country',
  projectName: 'mvp_customer_crew_project_name',
  orgName: 'mvp_customer_org_name',
  procedureSlug: 'mvp_customer_org_procedure_slug',
  dataNotice: 'mvp_customer_crew_data_notice',
  scheduleStart: 'mvp_customer_crew_schedule_start',
  scheduleEnd: 'mvp_customer_crew_schedule_end',
};

/** Keys that survive even when there is no personal crew invite token. */
const ORG_LEVEL_KEYS = new Set([
  CREW_CTX_KEYS.procedureSlug,
  CREW_CTX_KEYS.projectName,
  CREW_CTX_KEYS.orgName,
]);

export function storeCrewParticipantContext({
  orgName,
  orgRef,
  procedureSlug,
  crewToken,
  crewName,
  crewEmail,
  crewPhone,
  jobTitle,
  department,
  country,
  projectName,
  dataNotice,
  scheduleStart,
  scheduleEnd,
}) {
  try {
    sessionStorage.setItem('mvp_customer_org_name', orgName || '');
    sessionStorage.setItem('mvp_customer_org_ref', orgRef || '');
    if (procedureSlug) {
      sessionStorage.setItem(CREW_CTX_KEYS.procedureSlug, procedureSlug);
      sessionStorage.setItem('mvp_procedure_slug', procedureSlug);
    }
    // Always keep production name - NDA / home UI need it with or without a crew token.
    const project = String(projectName || '').trim();
    if (project) {
      sessionStorage.setItem(CREW_CTX_KEYS.projectName, project);
      try {
        localStorage.setItem('mvp_participant_project_name', project);
      } catch {
        /* ignore */
      }
    }

    if (crewToken) {
      sessionStorage.setItem(CREW_CTX_KEYS.token, crewToken);
      sessionStorage.setItem(CREW_CTX_KEYS.name, crewName || '');
      sessionStorage.setItem(CREW_CTX_KEYS.email, crewEmail || '');
      sessionStorage.setItem(CREW_CTX_KEYS.phone, crewPhone || '');
      sessionStorage.setItem(CREW_CTX_KEYS.jobTitle, jobTitle || '');
      sessionStorage.setItem(CREW_CTX_KEYS.department, department || '');
      sessionStorage.setItem(CREW_CTX_KEYS.country, country || '');
      sessionStorage.setItem(CREW_CTX_KEYS.dataNotice, dataNotice || '');
      if (scheduleStart) sessionStorage.setItem(CREW_CTX_KEYS.scheduleStart, scheduleStart);
      if (scheduleEnd) sessionStorage.setItem(CREW_CTX_KEYS.scheduleEnd, scheduleEnd);
    } else {
      Object.values(CREW_CTX_KEYS).forEach((k) => {
        if (!ORG_LEVEL_KEYS.has(k)) sessionStorage.removeItem(k);
      });
    }
  } catch {
    // ignore quota errors
  }
}

/** Production / project name from invite session (works without crew token). */
export function getParticipantProjectName() {
  try {
    return (
      sessionStorage.getItem(CREW_CTX_KEYS.projectName) ||
      localStorage.getItem('mvp_participant_project_name') ||
      ''
    ).trim();
  } catch {
    return '';
  }
}

/** Persist production name for NDA / home (session + local backup). */
export function persistParticipantProjectName(projectName) {
  const name = String(projectName || '').trim();
  if (!name) return '';
  try {
    sessionStorage.setItem(CREW_CTX_KEYS.projectName, name);
    localStorage.setItem('mvp_participant_project_name', name);
  } catch {
    /* ignore quota */
  }
  return name;
}

export function getCrewParticipantContext() {
  try {
    const token = sessionStorage.getItem(CREW_CTX_KEYS.token) || '';
    const projectName = sessionStorage.getItem(CREW_CTX_KEYS.projectName) || '';
    const procedureSlug = sessionStorage.getItem(CREW_CTX_KEYS.procedureSlug) || '';
    const orgName =
      sessionStorage.getItem(CREW_CTX_KEYS.orgName) ||
      sessionStorage.getItem('mvp_customer_org_name') ||
      '';

    // No personal invite token - still return org/project context when available.
    if (!token) {
      if (!projectName && !procedureSlug && !orgName) return null;
      return {
        token: '',
        name: '',
        email: '',
        phone: '',
        jobTitle: '',
        department: '',
        country: '',
        projectName,
        orgName,
        procedureSlug,
        dataNotice: '',
        scheduleStart: '',
        scheduleEnd: '',
      };
    }

    return {
      token,
      name: sessionStorage.getItem(CREW_CTX_KEYS.name) || '',
      email: sessionStorage.getItem(CREW_CTX_KEYS.email) || '',
      phone: sessionStorage.getItem(CREW_CTX_KEYS.phone) || '',
      jobTitle: sessionStorage.getItem(CREW_CTX_KEYS.jobTitle) || '',
      department: sessionStorage.getItem(CREW_CTX_KEYS.department) || '',
      country: sessionStorage.getItem(CREW_CTX_KEYS.country) || '',
      projectName,
      orgName,
      procedureSlug,
      dataNotice: sessionStorage.getItem(CREW_CTX_KEYS.dataNotice) || '',
      scheduleStart: sessionStorage.getItem(CREW_CTX_KEYS.scheduleStart) || '',
      scheduleEnd: sessionStorage.getItem(CREW_CTX_KEYS.scheduleEnd) || '',
    };
  } catch {
    return null;
  }
}

export function crewFirstName(fullName) {
  const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
  return parts[0] || '';
}

export function crewLastName(fullName) {
  const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return '';
  return parts.slice(1).join(' ');
}

export function intakePrefillFromCrew(ctx) {
  if (!ctx) return {};
  const patch = {};
  const first = crewFirstName(ctx.name);
  const last = crewLastName(ctx.name);
  if (first) patch['in.app.given_name'] = first;
  if (last) patch['in.app.surname1'] = last;
  if (ctx.email) patch['in.app.email'] = ctx.email;
  if (ctx.country) patch['in.app.nationality'] = ctx.country;
  return patch;
}
